import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Button, FormControl, Table, Spinner, Modal } from "react-bootstrap";
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useSortBy,
} from "react-table";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useDispatch } from "react-redux";
import { Fn_FillListData } from "../../store/Functions";
import { Container } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const DriverForApproval = () => {
  // State management
  const [state, setState] = useState({});
  const [gridData, setGridData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageModal, setImageModal] = useState({ show: false, url: "", label: "" });
  const dispatch = useDispatch();
  const breadCrumbTitle = "Driver For Approval";
  const breadcrumbItem = "Driver For Approval";

  const API_URL = `${API_WEB_URLS.MASTER}/0/token/DriverForApproval`;
  const API_URL_Approval = `${API_WEB_URLS.MASTER}/0/token/ApproveDriver`;

  useEffect(() => {
    setLoading(true);
    Fn_FillListData(dispatch, setGridData, "gridData", API_URL + "/Id/0")
      .finally(() => setLoading(false));
  }, [dispatch, API_URL]);

  const data = useMemo(() => gridData || [], [gridData]);

  const buildImageUrl = (imageName) => {
    if (!imageName) return "";
    return `${API_WEB_URLS.IMAGEURL}${imageName}`;
  };

  const handleApprovalClick = (rowData, isReject = false) => {
    const val = isReject ? 2 : 1;
    console.log(isReject ? "Reject row data:" : "Approval row data:", rowData);

    Fn_FillListData(
      dispatch,
      setState,
      "nothing",
      `${API_URL_Approval}/${val}/${rowData.Id}`
    ).finally(() => {
      // Refresh list after approve/reject
      Fn_FillListData(dispatch, setGridData, "gridData", API_URL + "/Id/0");
    });
  };
  
  const documentKeys = useMemo(
    () => [
      { key: "AadharFront", label: "Aadhar Front" },
      { key: "AadharBack", label: "Aadhar Back" },
      { key: "RcFront", label: "RC Front" },
      { key: "RCBack", label: "RC Back" },
      { key: "DrivingLicenseFront", label: "DL Front" },
      { key: "DrivingLicenseBack", label: "DL Back" },
      { key: "Insurance", label: "Insurance" },
      { key: "PUC", label: "PUC" },
    ],
    []
  );

  const columns = useMemo(
    () => {
      const baseColumns = [
      {
        Header: "Name",
        accessor: "Name",
        Cell: ({ value }) => <span className="fw-semibold">{value || "-"}</span>,
      },
      { Header: "Father Name", accessor: "FatherName", Cell: ({ value }) => value || "-" },
      { Header: "Email", accessor: "Email", Cell: ({ value }) => value || "-" },
      { Header: "Mobile", accessor: "MobileNo", Cell: ({ value }) => value || "-" },
      { Header: "PAN", accessor: "PanNo", Cell: ({ value }) => value || "-" },
      { Header: "Aadhar", accessor: "AadharNo", Cell: ({ value }) => value || "-" },
      { Header: "Driving Licence", accessor: "DrivingLicence", Cell: ({ value }) => value || "-" },
      { Header: "Pincode", accessor: "Pincode", Cell: ({ value }) => value || "-" },
      ];

      const documentColumns = documentKeys.map((d) => ({
        Header: d.label,
        accessor: d.key, // value will be the image filename
        disableSortBy: true,
        Cell: ({ value }) => {
          if (!value) return <span className="text-muted">-</span>;
          const url = buildImageUrl(value);
          if (!url) return <span className="text-muted">-</span>;

          return (
            <div
              className="text-center"
              style={{ cursor: "pointer", minWidth: 52 }}
              onClick={() => setImageModal({ show: true, url, label: d.label })}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && setImageModal({ show: true, url, label: d.label })}
              title={`${d.label} - Click to view`}
            >
              <img
                src={url}
                alt={d.label}
                style={{
                  width: 40,
                  height: 40,
                  objectFit: "cover",
                  borderRadius: 4,
                  border: "1px solid #dee2e6",
                }}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
          );
        },
      }));

      const approvalColumn = {
        Header: "Action",
        id: "approval",
        disableSortBy: true,
        Cell: ({ row }) => (
          <div className="d-flex gap-1">
            <Button
              size="sm"
              variant="success"
              onClick={() => handleApprovalClick(row.original, false)}
            >
              Approval
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={() => handleApprovalClick(row.original, true)}
            >
              Reject
            </Button>
          </div>
        ),
      };

      return [...baseColumns, ...documentColumns, approvalColumn];
    },
    [documentKeys]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, globalFilter },
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: { 
        pageIndex: 0, 
        pageSize: 10,
        sortBy: [
          {
            id: 'Name',
            desc: false
          }
        ]
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  if (loading) {
    return (
      <div className="master-list-loading d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <Spinner animation="border" role="status" className="mb-3">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="text-muted">Loading drivers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="master-list-container" style={{ fontSize: "0.875rem" }}>
      <Container fluid className="px-2 px-md-3 py-2">
        <Breadcrumbs title={breadCrumbTitle} breadcrumbItem={breadcrumbItem} />

     
        {/* Controls Section - compact */}
        <div className="master-list-controls mb-2">
          <Row className="align-items-center g-2">
            <Col md={6} sm={12}>
              <FormControl
                type="text"
                placeholder="Search driver..."
                value={globalFilter || ""}
                onChange={(e) => setGlobalFilter(e.target.value)}
                size="sm"
                className="form-control"
                style={{ maxWidth: 260 }}
              />
            </Col>
          </Row>
        </div>

        {/* Table Section - compact */}
        <div className="master-list-table-container mb-2">
          <div className="table-responsive">
            <Table {...getTableProps()} className="master-list-table table-hover table-sm" size="sm">
              <thead className="custom-header">
                {headerGroups.map((headerGroup, headerGroupIndex) => (
                  <tr {...headerGroup.getHeaderGroupProps()} key={headerGroupIndex}>
                    {headerGroup.headers.map((column, columnIndex) => (
                      <th
                        {...column.getHeaderProps(column.disableSortBy ? {} : column.getSortByToggleProps())}
                        key={columnIndex}
                        className="align-middle py-2 px-2"
                        style={{
                          backgroundColor: "#6D68CB",
                          color: "white",
                          borderColor: "#6D68CB",
                          fontSize: "0.8rem",
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span className="fw-semibold">{column.render("Header")}</span>
                          {!column.disableSortBy && (
                            <span className="sort-indicator">
                              {column.isSorted
                                ? column.isSortedDesc
                                  ? <i className="fas fa-sort-down" style={{ color: "white", fontSize: "0.7rem" }}></i>
                                  : <i className="fas fa-sort-up" style={{ color: "white", fontSize: "0.7rem" }}></i>
                                : <i className="fas fa-sort" style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.7rem" }}></i>}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="text-center py-4 text-muted" style={{ fontSize: "0.8rem" }}>
                      No drivers found.
                    </td>
                  </tr>
                ) : (
                  page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()} key={row.id} className="align-middle">
                        {row.cells.map((cell, cellIndex) => (
                          <td {...cell.getCellProps()} key={cellIndex} className="py-1 px-2" style={{ fontSize: "0.8rem" }}>
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </div>

        {/* Pagination Section - compact */}
        <div className="master-list-pagination py-2">
          <Row className="align-items-center g-2">
            <Col md={6} sm={12}>
              <div className="d-flex flex-wrap align-items-center gap-2">
                <span className="text-muted" style={{ fontSize: "0.8rem" }}>
                  Page <strong>{pageIndex + 1}</strong> of <strong>{pageCount || 1}</strong>
                </span>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  style={{ width: 56, fontSize: "0.8rem" }}
                  min={1}
                  max={pageCount || 1}
                  value={pageIndex + 1}
                  onChange={(e) => {
                    const val = e.target.value ? parseInt(e.target.value, 10) : 1;
                    const p = Math.min(Math.max(1, val), pageCount || 1) - 1;
                    gotoPage(p);
                  }}
                />
                <select
                  className="form-select form-select-sm"
                  style={{ width: 64, fontSize: "0.8rem" }}
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {[10, 20, 30, 40, 50].map((ps) => (
                    <option key={ps} value={ps}>{ps}</option>
                  ))}
                </select>
              </div>
            </Col>
            <Col md={6} sm={12} className="text-md-end">
              <div className="d-flex gap-1 justify-content-md-end">
                <Button onClick={() => gotoPage(0)} disabled={!canPreviousPage} size="sm" variant="outline-secondary"><i className="fas fa-angle-double-left"></i></Button>
                <Button onClick={() => previousPage()} disabled={!canPreviousPage} size="sm" variant="outline-secondary"><i className="fas fa-angle-left"></i></Button>
                <Button onClick={() => nextPage()} disabled={!canNextPage} size="sm" variant="outline-secondary"><i className="fas fa-angle-right"></i></Button>
                <Button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage} size="sm" variant="outline-secondary"><i className="fas fa-angle-double-right"></i></Button>
              </div>
            </Col>
          </Row>
        </div>
      </Container>

      {/* Image preview modal - full screen zoom */}
      <Modal
        show={imageModal.show}
        onHide={() => setImageModal({ show: false, url: "", label: "" })}
        fullscreen
        className="driver-doc-modal"
      >
        <Modal.Header closeButton className="bg-dark border-secondary">
          <Modal.Title className="text-white">{imageModal.label}</Modal.Title>
        </Modal.Header>
        <Modal.Body
          className="d-flex align-items-center justify-content-center p-0 bg-dark overflow-auto"
          style={{ minHeight: "calc(100vh - 60px)" }}
        >
          {imageModal.url && (
            <img
              src={imageModal.url}
              alt={imageModal.label}
              style={{
                maxWidth: "100%",
                maxHeight: "calc(100vh - 60px)",
                width: "auto",
                height: "auto",
                objectFit: "contain",
              }}
              onError={(e) => {
                e.currentTarget.src = "";
                e.currentTarget.alt = "Image not found";
              }}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};
export default DriverForApproval;
